<template>
  <section id="section-how-to-buy" class="buy" ref="root">
    <div class="buy__inner" :class="{ 'buy__inner_visible': inView }">
      <h2 class="buy__headline">Как купить картину?</h2>
      <div class="buy__timeline">
        <div
          class="buy__step"
          v-for="(step, i) in steps"
          :key="step.text"
          :style="{ transitionDelay: `${0.06 + i * 0.07}s` }"
        >
          <span class="buy__num">{{ i + 1 }}</span>
          <span class="buy__icon" v-html="step.icon" aria-hidden="true" />
          <div class="buy__content">
            <button v-if="step.openModal" type="button" class="buy__btn" @click="openOrderModal">{{ step.text }}</button>
            <a v-else-if="step.action" :href="step.action" class="buy__btn">{{ step.text }}</a>
            <span v-else class="buy__text">{{ step.text }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'HomeHowToBuy',
  setup() {
    const store = useStore()
    const root = ref(null)
    const inView = ref(false)
    let observer = null

    const steps = [
      {
        text: 'Выберите размер и параметры картины',
        icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>`
      },
      {
        text: 'Отправьте запрос',
        icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>`
      },
      {
        text: 'Обсуждаем детали и сроки',
        icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`
      },
      {
        text: 'Заказать',
        openModal: true,
        icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>`
      }
    ]

    onMounted(() => {
      observer = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) inView.value = true },
        { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
      )
      if (root.value) observer.observe(root.value)
    })
    onUnmounted(() => observer?.disconnect())

    function openOrderModal() {
      store.commit('setOrderModalOpen', true)
    }

    return { root, inView, steps, openOrderModal }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/_variables.scss";

$buy-duration: 0.6s;
$buy-ease: cubic-bezier(0.22, 1, 0.36, 1);

.buy {
  position: relative;
  padding: 100px $gap 100px;
  background: #0d0d0d;
  color: $color-primary-inverse;

  @media (max-width: $breakpoint-mobile) {
    padding: 64px $gap 72px;
  }

  &__inner {
    position: relative;
    z-index: 1;
    max-width: $container-max;
    margin: 0 auto;

    &_visible .buy__headline { opacity: 1; transform: translateY(0); }
    &_visible .buy__step { opacity: 1; transform: translateY(0); }
  }

  &__headline {
    font-size: clamp(14px, 1.5vw, 16px);
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    margin: 0 0 40px;
    opacity: 0;
    transform: translateY(20px);
    transition: opacity $buy-duration $buy-ease, transform $buy-duration $buy-ease;
  }

  &__timeline {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 0;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    overflow: hidden;

    @media (max-width: $breakpoint-mobile) {
      flex-direction: column;
      align-items: center;
      padding: 0;
      background: transparent;
      border: none;
    }
  }

  &__step {
    position: relative;
    flex: 1;
    min-width: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 28px 20px;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    text-align: center;

    &:last-child {
      border-right: none;
    }

    opacity: 0;
    transform: translateY(16px);
    transition: opacity $buy-duration $buy-ease, transform $buy-duration $buy-ease,
      background 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.04);
    }

    @media (max-width: 900px) {
      min-width: 50%;
      border-right: none;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);

      &:nth-child(odd) {
        border-right: 1px solid rgba(255, 255, 255, 0.08);
      }
      &:nth-last-child(-n+2) {
        border-bottom: none;
      }
    }

    @media (max-width: $breakpoint-mobile) {
      min-width: 0;
      width: 100%;
      max-width: 320px;
      padding: 24px 20px;
      border-right: none;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.06);
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }

      &:not(:last-child) {
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }
    }
  }

  &__num {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.15);
    color: $color-primary-inverse;
    font-size: 13px;
    font-weight: 800;
    border-radius: 4px;
  }

  &__icon {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100px;
    height: 100px;
    margin-bottom: 20px;
    color: rgba(255, 255, 255, 0.9);

    :deep(svg) {
      width: 56px;
      height: 56px;
    }

    @media (max-width: $breakpoint-mobile) {
      width: 96px;
      height: 96px;
      margin-bottom: 18px;
      :deep(svg) {
        width: 56px;
        height: 56px;
      }
    }
  }

  &__content {
    text-align: center;
  }

  &__text {
    font-size: clamp(14px, 1.4vw, 16px);
    font-weight: 600;
    line-height: 1.35;
    color: rgba(255, 255, 255, 0.95);
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    padding: 10px 20px;
    background: $color-primary-inverse;
    color: $color-primary;
    font-size: 15px;
    font-weight: 700;
    text-decoration: none;
    border-radius: 50px;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(255, 255, 255, 0.2);
    }
  }

}

.buy__inner_visible .buy__headline { opacity: 0.7 !important; }
</style>
